/**
 * The two sales documents, as data.
 *
 * A quotation and a proforma invoice are the same document with different
 * headings: same meta row, same party box, same line items, same plate and die
 * charges, same summary. Only the wording, one spec field and one meta field
 * differ. Keeping that difference in a config rather than in a second copy of
 * the page is the whole reason there is one editor and one list.
 *
 * Field names and defaults come from the sales-documents contract; the wording
 * and the section order come from the printed HTML the office already uses.
 */

import { FileSpreadsheet, Receipt } from 'lucide-react';

const COMMON_SPECS = [
  ['product_name', 'Label / Product Name'],
  ['label_size', 'Label Size (mm)', 'W × H mm'],
];

export const QUOTATION = {
  key: 'quotation',
  api: '/quotations',
  route: '/quotations',
  listTitle: 'Quotations',
  docTitle: 'Quotation',
  i18nKey: 'quotations',
  icon: FileSpreadsheet,
  emptyList: 'Nothing quoted yet — raise the first one from the button above',
  numberLabel: 'Quotation No.',
  numberPlaceholder: 'QTN-2526-001',
  partyHeading: 'Quote To',
  contactLabel: 'Contact Person & Phone',
  contactPlaceholder: 'Name | +91 XXXXX XXXXX',
  specsHeading: 'Label Specifications',
  itemsHeading: 'Pricing Breakup',
  chargesHeading: 'One-Time Charges',
  chargeInfoLabel: 'Additional Info',
  termsHeading: 'Terms & Conditions',
  notesHeading: 'Notes / Remarks',
  notesPlaceholder: 'Any special notes, artwork requirements, or additional remarks…',
  footerNote: 'This is a computer-generated quotation. Prices are subject to change without prior notice.',
  // "Ref / Enquiry No." is the enquiry this price answers — a proforma has none
  hasReference: true,
  specFields: [
    ...COMMON_SPECS,
    ['material', 'Material / Substrate', 'BOPP / Paper / PET / PE…'],
    ['colours_finish', 'No. of Colours & Finish', '4C + Gloss Lamination'],
    ['shape_features', 'Shape / Special Features', 'Die-cut / Hot Foil / Emboss…'],
  ],
  terms: [
    'Prices valid for 30 days from quotation date.',
    '100% advance payment before production.',
    'Plate & die charges are one-time and non-refundable.',
    'Delivery: 10-15 working days after artwork approval.',
    '±5% quantity variation is acceptable.',
    'GST extra as applicable.',
    'Subject to Hoshiarpur jurisdiction.',
  ],
};

export const PROFORMA_INVOICE = {
  key: 'proforma_invoice',
  api: '/proforma-invoices',
  route: '/proforma-invoices',
  listTitle: 'Proforma Invoices',
  docTitle: 'Proforma Invoice',
  i18nKey: 'proformaInvoices',
  icon: Receipt,
  emptyList: 'No proforma invoices yet — raise the first one from the button above',
  numberLabel: 'Invoice No.',
  numberPlaceholder: 'PI-2526-001',
  partyHeading: 'Bill To',
  contactLabel: 'Contact',
  contactPlaceholder: '+91 XXXXX XXXXX',
  specsHeading: 'Order Details',
  itemsHeading: 'Line Items',
  chargesHeading: 'Plate & Die Charges',
  chargeInfoLabel: 'Remarks',
  termsHeading: 'Payment & Terms',
  notesHeading: 'Notes / Special Instructions',
  notesPlaceholder: 'Artwork notes, special instructions, or any additional remarks…',
  footerNote: 'This is a computer-generated proforma invoice. This is not a tax invoice.',
  hasReference: false,
  specFields: [
    ...COMMON_SPECS,
    ['material', 'Material', 'BOPP / Paper / PET / PE…'],
    ['colours_finish', 'Colours & Finish', '4C + Gloss Lamination'],
    ['delivery_terms', 'Delivery Terms', 'Ex-Works / Door Delivery'],
  ],
  terms: [
    '100% advance payment before production.',
    'Plate & die charges are non-refundable.',
    'Delivery: 10-15 working days after approval.',
    '±5% quantity variation is acceptable.',
    'Subject to Hoshiarpur jurisdiction.',
  ],
};

export const DOCUMENT_TYPES = [QUOTATION, PROFORMA_INVOICE];

export const COMPANY = {
  unit: 'Label Unit',
  address: 'Jalandhar Road, Behind KIA Showroom, Kurangana Link Road, Hoshiarpur (Pb.) 146022',
  phones: '+91 94783-19689  |  92166-12100  |  info@labelify.co.in',
  gstin: '03AAGCL4622E1ZZ',
};

export const BANK = [
  ['A/c Holder', 'LABELIFY PRIVATE LIMITED'],
  ['Bank Name', 'Punjab National Bank'],
  ['Account No.', '0206002100563865'],
  ['IFSC Code', 'PUNB0020600'],
  ['Branch', 'Hoshiarpur, Railway Road'],
];

export const STATUSES = [
  ['draft', 'Draft'],
  ['sent', 'Sent'],
  ['accepted', 'Accepted'],
  ['rejected', 'Rejected'],
  ['expired', 'Expired'],
  ['converted', 'Converted'],
];

export const STATUS_TONE = {
  draft: 'text-slate-500',
  sent: 'text-brand-blue',
  accepted: 'text-emerald-600',
  rejected: 'text-red-600',
  expired: 'text-amber-600',
  converted: 'text-doc-die',
};
